import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { RootLayout } from './routes/layout.js';
import { HomeScreen } from './routes/home.js';
import { InventorySearchScreen } from './routes/inventory-search.js';
import { InventoryDetailScreen } from './routes/inventory-detail.js';
import { CustomersSearchScreen } from './routes/customers-search.js';
import { CustomersDetailScreen } from './routes/customers-detail.js';
import { SalesOrdersSearchScreen } from './routes/sales-orders-search.js';
import { SalesOrdersDetailScreen } from './routes/sales-orders-detail.js';
import { QuoteBuilderScreen } from './routes/quote-builder.js';
import { OutboxScreen } from './routes/outbox.js';
import { DispatchScreen } from './routes/dispatch.js';
import { JobsScreen } from './routes/jobs.js';
import { MeScreen } from './routes/me.js';
import { TruckScreen } from './routes/truck.js';
import { TrucksScreen } from './routes/trucks.js';
import { NewJobScreen } from './routes/new-job.js';
import { AdminScreen } from './routes/admin.js';
import { ContractsScreen } from './routes/contracts.js';
import { LoginScreen } from './routes/login.js';
import { useAuth } from './lib/auth-store.js';

// Routes that don't require an existing session. Cognito SRP auth is
// direct (no redirect callback), so just /login.
const UNAUTH_ROUTES = new Set(['/login']);

// Office surfaces — field techs are routed away from these to their own
// landing (/jobs). The web nav already hides the links, but a direct URL
// hit would otherwise render an empty/broken screen. Mirror server-side
// RolesGuard so deep links stay in sync.
const OFFICE_ONLY_PREFIXES = ['/', '/customers', '/operations', '/contracts', '/admin'];
const ADMIN_ONLY_PREFIXES = ['/admin'];

const rootRoute = createRootRoute({
  component: RootLayout,
  beforeLoad: ({ location }) => {
    if (UNAUTH_ROUTES.has(location.pathname)) return;
    const auth = useAuth.getState();
    if (!auth.isAuthenticated()) {
      throw redirect({ to: '/login' });
    }
    const role = auth.user?.role;
    if (!role) return;
    if (role === 'field_tech') {
      // Field techs landing on /, /customers, /operations, etc. — bounce
      // them to /jobs so they don't see an empty office surface.
      const hit = OFFICE_ONLY_PREFIXES.some((p) =>
        p === '/' ? location.pathname === '/' : location.pathname.startsWith(p),
      );
      if (hit) {
        throw redirect({ to: '/jobs' });
      }
    } else if (role !== 'admin') {
      // dispatcher + office can hit everything except /admin.
      const hit = ADMIN_ONLY_PREFIXES.some((p) => location.pathname.startsWith(p));
      if (hit) {
        throw redirect({ to: '/' });
      }
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeScreen,
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  component: InventorySearchScreen,
  validateSearch: (raw: Record<string, unknown>): { whse?: string; q?: string } => {
    const result: { whse?: string; q?: string } = {};
    if (typeof raw.whse === 'string' && raw.whse.length > 0) result.whse = raw.whse;
    if (typeof raw.q === 'string' && raw.q.length > 0) result.q = raw.q;
    return result;
  },
});

const inventoryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/$id',
  component: InventoryDetailScreen,
  validateSearch: (raw: Record<string, unknown>): { customerNo?: string } => {
    const result: { customerNo?: string } = {};
    if (typeof raw.customerNo === 'string' && raw.customerNo.length > 0) {
      result.customerNo = raw.customerNo;
    }
    return result;
  },
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) {
      throw redirect({ to: '/inventory' });
    }
  },
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: CustomersSearchScreen,
  validateSearch: (raw: Record<string, unknown>): { q?: string; customerNo?: string } => {
    const result: { q?: string; customerNo?: string } = {};
    if (typeof raw.q === 'string' && raw.q.length > 0) result.q = raw.q;
    if (typeof raw.customerNo === 'string' && raw.customerNo.length > 0) {
      result.customerNo = raw.customerNo;
    }
    return result;
  },
});

const customersDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$id',
  component: CustomersDetailScreen,
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) {
      throw redirect({ to: '/customers' });
    }
  },
});

// /sales-orders existed as a separate list+search surface. Its function
// is now folded into /operations. Redirect bookmarks with the old
// status=O/P/type=Q query string translated into the new status= shorthand.
const salesOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales-orders',
  beforeLoad: ({ search }) => {
    const raw = search as Record<string, unknown>;
    const customerNo = typeof raw.customerNo === 'string' ? raw.customerNo : undefined;
    const orderNo = typeof raw.orderNo === 'string' ? raw.orderNo : undefined;
    const status = typeof raw.status === 'string' ? raw.status : undefined;
    const type = typeof raw.type === 'string' ? raw.type : undefined;
    const view =
      type === 'Q' ? 'quotes' : status === 'P' ? 'invoiced' : 'open';
    throw redirect({
      to: '/operations',
      search: {
        ...(view === 'open' ? {} : { status: view }),
        ...(customerNo ? { customerNo } : {}),
        ...(orderNo ? { orderNo } : {}),
      },
    });
  },
  component: SalesOrdersSearchScreen,
  validateSearch: (raw: Record<string, unknown>) => raw,
});

const salesOrdersDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales-orders/$id',
  component: SalesOrdersDetailScreen,
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) {
      throw redirect({ to: '/sales-orders' });
    }
  },
});

const quoteBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/quote-builder',
  component: QuoteBuilderScreen,
  validateSearch: (
    raw: Record<string, unknown>,
  ): { customerNo?: string; addWhse?: string; addPartNo?: string } => {
    const result: { customerNo?: string; addWhse?: string; addPartNo?: string } = {};
    if (typeof raw.customerNo === 'string' && raw.customerNo.length > 0) {
      result.customerNo = raw.customerNo;
    }
    if (typeof raw.addWhse === 'string' && raw.addWhse.length > 0) {
      result.addWhse = raw.addWhse;
    }
    if (typeof raw.addPartNo === 'string' && raw.addPartNo.length > 0) {
      result.addPartNo = raw.addPartNo;
    }
    return result;
  },
});

const outboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/outbox',
  component: OutboxScreen,
});

// /operations is the merged inbox + board. /dispatch is preserved as a
// redirect so existing links and the previous bookmark URLs keep working.
const operationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations',
  component: DispatchScreen,
  validateSearch: (
    raw: Record<string, unknown>,
  ): {
    status?: 'open' | 'quotes' | 'invoiced';
    customerNo?: string;
    orderNo?: string;
    trade?: string;
  } => {
    const result: {
      status?: 'open' | 'quotes' | 'invoiced';
      customerNo?: string;
      orderNo?: string;
      trade?: string;
    } = {};
    if (raw.status === 'open' || raw.status === 'quotes' || raw.status === 'invoiced') {
      result.status = raw.status;
    }
    if (typeof raw.customerNo === 'string' && raw.customerNo.length > 0) {
      result.customerNo = raw.customerNo;
    }
    if (typeof raw.orderNo === 'string' && raw.orderNo.length > 0) {
      result.orderNo = raw.orderNo;
    }
    if (typeof raw.trade === 'string' && raw.trade.length > 0) result.trade = raw.trade;
    return result;
  },
});

const dispatchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dispatch',
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/operations', search: search as Record<string, unknown> });
  },
  component: DispatchScreen,
  validateSearch: (raw: Record<string, unknown>) => raw,
});

const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs',
  component: JobsScreen,
});

// Field-created jobs ("Enercare mode") — available to techs; gated by the
// fieldJobs setting inside the screen and server-side.
const newJobRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/new',
  component: NewJobScreen,
});

const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: MeScreen,
});

const truckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/truck',
  component: TruckScreen,
});

const trucksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trucks',
  component: TrucksScreen,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminScreen,
});

const contractsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contracts',
  component: ContractsScreen,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  homeRoute,
  inventoryRoute,
  inventoryDetailRoute,
  customersRoute,
  customersDetailRoute,
  salesOrdersRoute,
  salesOrdersDetailRoute,
  quoteBuilderRoute,
  outboxRoute,
  operationsRoute,
  dispatchRoute,
  jobsRoute,
  newJobRoute,
  meRoute,
  truckRoute,
  trucksRoute,
  adminRoute,
  contractsRoute,
]);

/**
 * Route-scoped error surface — renders INSIDE the layout so a single bad
 * payload on one screen no longer blows away the nav shell to the
 * unstyled root Sentry fallback (audit quick-win). Sentry still hears
 * about it via the root boundary's breadcrumb trail on rethrow-worthy
 * cases; here we favor recovery.
 */
function RouteErrorScreen({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="max-w-md mx-auto py-10 space-y-3 text-center">
      <h1 className="text-lg font-semibold text-ink">This screen hit an error.</h1>
      <p className="text-sm text-ink-muted break-words">{error.message}</p>
      <button
        type="button"
        onClick={reset}
        className="rounded-md px-3 py-2 text-sm font-medium text-white"
        style={{ backgroundColor: 'var(--color-text-info)' }}
      >
        Try again
      </button>
    </div>
  );
}

function NotFoundScreen() {
  return (
    <div className="max-w-md mx-auto py-10 space-y-2 text-center">
      <h1 className="text-lg font-semibold text-ink">Page not found</h1>
      <p className="text-sm text-ink-muted">
        That link doesn't match any Tinsmith screen. Use the navigation to get back on track.
      </p>
    </div>
  );
}

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: RouteErrorScreen,
  defaultNotFoundComponent: NotFoundScreen,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
